import type {
	FailedPendingResponse,
	FilterRuleModel,
	HTTPValidationError,
	ListInterpretationParams,
	ScenarioMatchedProductsCountResponseModel,
	ScenarioMatchedProductsParams,
	ScenarioUpdateDataModel,
	StatisticsInterpretabilityGroupedResponseModel,
	StatisticsInterpretabilityRequest,
	StatisticsInterpretabilityResponse,
	StatisticsRepricingRequest,
	StatisticsRepricingResponse,
	StatisticsRequest,
	StatisticsResponse,
	StatisticsTargetProtectDistributionRequest,
	TargetProtectDistributionModel,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'
import type { InterpretationStepModel } from '../model/types'

import { useQuery } from '@tanstack/react-query'
import { map, prop, uniqBy } from 'lodash/fp'

import { client } from '@/shared/api/client'

import { interpretationStepSchema } from '../model/contract'

export const optimizationStatisticsQueries = {
	statisticsInterpretability: (params: StatisticsInterpretabilityRequest) => ({
		queryKey: ['statistic-interpretability', params],
		queryFn: () => client.bi.getStatisticsInterpretability(params),
	}),
	statistics: (params: StatisticsRequest) => ({
		queryKey: ['statistic', params],
		queryFn: () => client.bi.getStatistics(params),
	}),
}

export const useInterpretationQuery = (
	params: ListInterpretationParams,
	options?: UseQueryOptions<InterpretationStepModel[], AxiosError>
) =>
	useQuery<InterpretationStepModel[], AxiosError>({
		queryKey: ['interpretation', params],
		queryFn: () =>
			client.api
				.listInterpretation(params)
				.then(map(interpretationStepSchema.parse)),
		staleTime: 60 * 1000 * 5,
		...(options || {}),
	})

export const useInterpretabilityStatisticQuery = <
	T =
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null,
>(
	body: StatisticsInterpretabilityRequest,
	options?: UseQueryOptions<
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null,
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null,
		AxiosError<HTTPValidationError>,
		T
	>({
		...optimizationStatisticsQueries.statisticsInterpretability(body),
		...(options || {}),
	})
export const useRepricingStatisticQuery = <
	T = StatisticsRepricingResponse | null,
>(
	body: StatisticsRepricingRequest,
	options?: UseQueryOptions<
		StatisticsRepricingResponse | null,
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<
		StatisticsRepricingResponse | null,
		AxiosError<HTTPValidationError>,
		T
	>({
		queryKey: ['statistic-repricing', body],
		queryFn: () => client.bi.getStatisticsRepricing(body),
		...(options || {}),
	})
export const useStatisticQuery = <T = StatisticsResponse>(
	body: StatisticsRequest,
	options?: UseQueryOptions<
		StatisticsResponse,
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<StatisticsResponse, AxiosError<HTTPValidationError>, T>({
		queryKey: ['statistic', body],
		queryFn: () => client.bi.getStatistics(body),
		...(options || {}),
	})

export const useStopListSuggestsQuery = <
	T = ScenarioMatchedProductsCountResponseModel,
>(
	params: ScenarioMatchedProductsParams,
	body: ScenarioUpdateDataModel,
	options?: UseQueryOptions<
		ScenarioMatchedProductsCountResponseModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>
) =>
	useQuery<
		ScenarioMatchedProductsCountResponseModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>({
		queryKey: ['stop-list', params, body],
		queryFn: () => client.scenario.scenarioMatchedProducts(params, body),
		...(options || {}),
	})

export const useStatisticsTargetProtectDistributionQuery = <
	T = TargetProtectDistributionModel[] | null,
>(
	body: StatisticsTargetProtectDistributionRequest,
	options?: UseQueryOptions<TargetProtectDistributionModel[] | null, Error, T>
) =>
	useQuery<TargetProtectDistributionModel[] | null, Error, T>({
		queryKey: ['statistics-target-protect-distribution', body],
		queryFn: () => client.bi.getStatisticsTargetProtectDistribution(body),
		...(options || {}),
	})

export const useOptimizationGroupsByProductFiltersQuery = <
	T = OptimizationGroupsByProductFiltersModel[] | null,
>(body: {
	filters: FilterRuleModel[]
}) =>
	useQuery<OptimizationGroupsByProductFiltersModel[] | null, Error, T>({
		queryKey: ['optimization-groups-by-product-filters', body],
		queryFn: async () => {
			const [optimizationGroupsByFilters, optimizations] = await Promise.all([
				client.products
					.getProductsCountByPc(body)
					.then((items) => uniqBy('optimization_group_id', items))
					.then(map(prop('optimization_group_id'))),
				client.optimizations.getCurrentOptimizations(),
			])
			const optimizationsMap = Object.fromEntries(
				optimizations.map((o) => [o.optimization_group_id, o])
			)

			return client.optimizationGroups.getOptimizationGroups().then(
				(optimizationGroups) =>
					optimizationGroups
						?.filter(({ id }) => optimizationGroupsByFilters.includes(id))
						.map(({ id, name }) => ({
							id,
							name,
							status: optimizationsMap?.[id]?.status || '',
							optimization: optimizationsMap?.[id]?.id || '',
						})) as OptimizationGroupsByProductFiltersModel[]
			)
		},
	})
type OptimizationGroupsByProductFiltersModel = {
	id: string
	name: string
	status: string
	optimization: string
}
